import React, {FormEvent, useCallback, useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {ActivityService} from "./activity.service";
import {
    Checkbox,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip
} from "@mui/material";
import {Check, Delete, Search} from "@mui/icons-material";
import {useNavigate} from "react-router-dom";
import CustomTextField from "../typescript/custom-text-field/custom-text-field";
import {AuthService} from "../typescript/auth/auth.service";
import {changePageTitle, setAlert} from "../typescript/redux/actions";
import {UserService} from "../user/user.service";
import {User} from "../user/user";
import {Activity} from "./activity";
import {DateUtil} from "../typescript/util/2.0.0/date-util";
import ForeignKey from "../typescript/foreign-key/foreign-key";
import {ColorButtonEnum} from "../typescript/util/color-button-enum";
import {ColorIconEnum} from "../typescript/util/color-icon-enum";
import CustomButtonGroup from "../typescript/custom-button-group/custom-button-group";
import CustomButton from "../typescript/custom-button/custom-button";
import {contentFormatted} from "../typescript/util/string-util";

export function ActivityList() {

    let navigate = useNavigate();
    const dispatch = useDispatch();
    const isAdmin = AuthService.userInRole("ADMIN");
    const userWithDetails = AuthService.getUserDetails();
    if (!userWithDetails) {
        throw new Error('User not logged');
    }
    const [startDate, setStartDate] = useState(DateUtil.toJavaSqlDate(DateUtil.currentDate()));
    const [endDate, setEndDate] = useState(DateUtil.toJavaSqlDate(DateUtil.currentDate()));
    const [user, setUser] = useState<User>(userWithDetails);
    const [items, setItems] = useState<Activity[]>([]);
    const [selectedItems, setSelectedItems] = useState<number[]>([]);

    const findAll = useCallback(async (startDateParam: string, endDateParam: string, userIdParam: number | undefined) => {
        if (!userIdParam) {
            throw new Error('User without id');
        }
        const items = isAdmin ? await ActivityService.findAll(startDateParam, endDateParam, userIdParam) :
            await ActivityService.findAllOfCurrentUser(startDate, endDate);
        setItems(items);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        dispatch(changePageTitle("Atividades"));
        findAll(startDate, endDate, user.id).then();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const insertItem = () => {
        navigate('/activity');
    }

    const deleteItem = (item: Activity) => {
        ActivityService.delete(item.id).then(() => {
            findAll(startDate, endDate, user.id).then();
        }).catch((error) => {
            dispatch(setAlert({
                show: true,
                message: 'Erro ao excluir registro - ' + error.message,
                severity: 'error'
            }));
        })
    }

    const deleteSelectedItems = () => {
        ActivityService.deleteByIds(selectedItems).then(() => {
            findAll(startDate, endDate, user.id).then();
            setSelectedItems([]);
        }).catch((error) => {
            dispatch(setAlert({
                show: true,
                message: 'Erro ao excluir registros - ' + error.message,
                severity: 'error'
            }));
        })
    }

    const accomplishedItem = (item: Activity) => {
        ActivityService.accomplished(item.id, new Date()).then(() => {
            findAll(startDate, endDate, user.id).then();
        }).catch(() => {

        })
    }

    const onChangeStartDate = (event: React.ChangeEvent<HTMLInputElement>) => {
        setStartDate(event.target.value)
    }

    const onChangeEndDate = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEndDate(event.target.value)
    }

    const onChangeUsuario = async (id: number) => {
        setUser({id: id});
        if (id) {
            const user = await UserService.findOne(id);
            if (user) {
                setUser(user)
            }
        }
    }

    const onSelectUsuario = (user: User) => {
        if (user) {
            setUser(user);
        }
    }

    const search = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (!user.id) {
            throw new Error('User without id');
        }
        const items = isAdmin ? await ActivityService.findAll(startDate, endDate, user.id) :
            await ActivityService.findAllOfCurrentUser(startDate, endDate);
        setItems(items);
    }

    const showAccomplishedItem = (item: Activity) => {
        return !item.accomplished;
    }

    let rowOperations;
    if (isAdmin) {
        rowOperations = [
            {color: () => ColorIconEnum.PRIMARY, onClick: deleteItem, label: () => 'Excluir', icon: () => <Delete/>},
            {
                color: () => ColorIconEnum.PRIMARY,
                onClick: accomplishedItem,
                label: () => 'Marcar como realizado',
                icon: () => <Check/>,
                show: showAccomplishedItem
            }
        ];
    } else {
        rowOperations = [{
            color: () => ColorIconEnum.PRIMARY,
            onClick: accomplishedItem,
            label: () => 'Marcar como realizado',
            icon: () => <Check/>,
            show: showAccomplishedItem
        }];
    }

    const handleSelectItem = (id: number) => {
        setSelectedItems(prevSelected =>
            prevSelected.includes(id) ? prevSelected.filter(item => item !== id) : [...prevSelected, id]
        );
    }

    const renderUserForeignKey = () => {
        return isAdmin && (
            <Grid item md xs={6}>
                <ForeignKey fullWidth autoFocus required label="Usuário" value={user} fieldKey={'id'}
                            fieldDescription={'username'} labelDescription='Usuário' onChange={onChangeUsuario}
                            onSelect={onSelectUsuario} findAll={UserService.findAll}
                            dataColumnNames={['ID', 'Usuário']} dataColumns={['id', 'username']}
                            dataTypes={['number', 'text']}/>
            </Grid>
        );
    }

    const renderResume = () => {
        const total = items.length;
        let totalAccomplished = 0;
        for (let i = 0; i < items.length; i++) {
            if (items[i].accomplished) {
                totalAccomplished++;
            }
        }
        const totalDontAccomplished = total - totalAccomplished;
        const percentual = Math.round(totalAccomplished / total * 100);
        return (
            <p><strong>Total atividades:</strong> {total | 0} - <strong>Atividades
                realizadas:</strong> {totalAccomplished | 0} - <strong>Atividades não
                realizadas:</strong> {totalDontAccomplished | 0} - <strong>Índice:</strong> {percentual | 0} %</p>
        )
    }

    const renderHeader = () => {
        return (
            <CustomButtonGroup>
                <CustomButton variant="outlined" type="button" color={ColorButtonEnum.PRIMARY} onClick={insertItem}>
                    Inserir
                </CustomButton>
                <CustomButton variant="outlined" type="button" color={ColorButtonEnum.PRIMARY} onClick={deleteSelectedItems} disabled={selectedItems.length === 0}>
                    Deletar Selecionados
                </CustomButton>
            </CustomButtonGroup>
        );
    }

    const renderRowOperations = (itemParam: Activity) => {
        return rowOperations.map((operation, keyIndex) => {
            return (
                <Tooltip key={keyIndex} title={operation.label()}>
                    <span>
                        <IconButton color={operation.color()}
                                    onClick={() => operation.onClick(itemParam)}>
                            {operation.icon()}
                        </IconButton>
                    </span>
                </Tooltip>
            )
        })
    }

    return (
        <React.Fragment>
            <form onSubmit={search}>
                <Grid container spacing={1}>
                    <Grid item md xs={6}>
                        <CustomTextField fullWidth required label="Data inicial" type="date" value={startDate || ''}
                                         InputLabelProps={{shrink: true}} onChange={onChangeStartDate}/>
                    </Grid>
                    <Grid item md xs={6}>
                        <CustomTextField fullWidth required label="Data final" type="date" value={endDate || ''}
                                         InputLabelProps={{shrink: true}} onChange={onChangeEndDate}/>
                    </Grid>
                    {renderUserForeignKey()}
                    <Grid item>
                        <Tooltip title="Pesquisar">
                            <IconButton type={"submit"}>
                                <Search/>
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
            </form>
            {isAdmin && renderResume()}
            {isAdmin && renderHeader()}
            <TableContainer component={Paper}>
                <Table size='small' stickyHeader>
                    <TableHead sx={{"& th": {fontWeight: 'bold'}}}>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    indeterminate={selectedItems.length > 0 && selectedItems.length < items.length}
                                    checked={items.length > 0 && selectedItems.length === items.length}
                                    onChange={(event) => setSelectedItems(event.target.checked ? items.map(item => item.id) : [])}
                                />
                            </TableCell>
                            <TableCell>Data</TableCell>
                            <TableCell>Descrição</TableCell>
                            <TableCell>Período</TableCell>
                            <TableCell>Realizado</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map((item, index) => (
                            <TableRow key={index}>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={selectedItems.includes(item.id)}
                                        onChange={() => handleSelectItem(item.id)}
                                    />
                                </TableCell>
                                <TableCell>{contentFormatted(item.date, 'date')}</TableCell>
                                <TableCell>{item.descricao}</TableCell>
                                <TableCell>{item.round}</TableCell>
                                <TableCell>{contentFormatted(item.accomplished, 'datetime')}</TableCell>
                                <TableCell>
                                    {renderRowOperations(item)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </React.Fragment>
    );
}